import React, { useState, useEffect, useCallback, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Modal, IconButton, Chip } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: 1, width: "100%", height: "100%",
    display: "flex", flexDirection: "column",
    overflow: "hidden", boxSizing: "border-box",
    background: theme.palette.type === "dark"
      ? "linear-gradient(145deg, #060D1A 0%, #0A1628 100%)"
      : "linear-gradient(145deg, #EBF3FF 0%, #F0F6FF 100%)",
  },
  hero: {
    flexShrink: 0, borderRadius: 16, padding: theme.spacing(1.5, 2.5),
    margin: theme.spacing(2, 2, 0),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #0A1628 0%, #0D1F3C 100%)"
      : "linear-gradient(135deg, #EBF3FF 0%, #D6E9FF 100%)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(0,140,255,0.18)" : "1px solid rgba(0,102,255,0.12)",
    boxShadow: "0 4px 16px rgba(0,102,255,0.07)",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: theme.spacing(2), position: "relative", overflow: "hidden",
  },
  heroBgCircle: {
    position: "absolute", right: -20, top: -20, width: 140, height: 140,
    borderRadius: "50%", pointerEvents: "none",
    background: "radial-gradient(circle, rgba(0,102,255,0.07) 0%, transparent 70%)",
  },
  heroLeft: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), zIndex: 1 },
  heroIcon: {
    width: 48, height: 48, borderRadius: 14, flexShrink: 0, fontSize: "1.3rem",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 6px 16px rgba(0,102,255,0.3)",
  },
  heroTitle: {
    fontWeight: 800, fontSize: "1.1rem", letterSpacing: "-0.3px",
    color: theme.palette.type === "dark" ? "#ffffff" : "#0A1628",
  },
  heroSubtitle: {
    fontSize: "0.75rem", marginTop: 2,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "rgba(10,22,40,0.55)",
  },
  heroChip: {
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.9)",
    border: "1px solid rgba(0,102,255,0.15)", borderRadius: 10,
    fontWeight: 700, fontSize: "0.72rem", zIndex: 1,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#0A1628",
  },
  grid: {
    flex: 1, overflowY: "auto", padding: theme.spacing(2),
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(2), alignContent: "start",
    ...theme.scrollbarStyles,
  },
  card: {
    borderRadius: 16,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    overflow: "hidden", cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 8px 24px rgba(0,102,255,0.15)",
      border: "1px solid rgba(0,102,255,0.25)",
    },
    "&:hover $playOverlay": { opacity: 1 },
  },
  thumbWrap: {
    position: "relative", width: "100%", paddingBottom: "56.25%",
    background: "#0A1628", overflow: "hidden",
  },
  thumb: {
    position: "absolute", top: 0, left: 0,
    width: "100%", height: "100%", objectFit: "cover",
    transition: "transform 0.3s ease",
  },
  playOverlay: {
    position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
    background: "rgba(0,102,255,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    opacity: 0, transition: "opacity 0.2s ease",
  },
  playBtn: {
    width: 52, height: 52, borderRadius: "50%",
    background: "#ffffff",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
  },
  cardBody: { padding: theme.spacing(1.5) },
  cardTitle: {
    fontWeight: 700, fontSize: "0.88rem", color: theme.palette.text.primary,
    marginBottom: 4, lineHeight: 1.3,
  },
  cardDesc: {
    fontSize: "0.75rem", color: theme.palette.text.secondary,
    lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis",
    display: "-webkit-box", "-webkit-line-clamp": 2, "-webkit-box-orient": "vertical",
  },
  emptyState: {
    gridColumn: "1 / -1",
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: theme.spacing(8), gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  // MODAL
  modalOverlay: {
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)",
  },
  modalContent: {
    outline: "none", width: "92%", maxWidth: 900,
    borderRadius: 20, overflow: "hidden",
    background: "#000",
    boxShadow: "0 24px 60px rgba(0,0,0,0.7)",
    position: "relative",
  },
  modalHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px",
    background: "linear-gradient(135deg, #0066FF, #00A3FF)",
  },
  modalTitle: {
    fontWeight: 700, fontSize: "0.9rem", color: "#fff",
  },
  modalClose: {
    color: "#fff", padding: 6,
    "&:hover": { background: "rgba(255,255,255,0.2)" },
    borderRadius: 8,
  },
  iframeWrap: {
    position: "relative", width: "100%", paddingBottom: "56.25%",
  },
  iframe: {
    position: "absolute", top: 0, left: 0,
    width: "100%", height: "100%", border: "none",
  },
}));

const Helps = () => {
  const classes = useStyles();
  const [records, setRecords] = useState([]);
  const { list } = useHelps();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");

  useEffect(() => {
    async function fetchData() {
      const helps = await list();
      setRecords(helps);
    }
    fetchData();
  }, []);

  const openVideoModal = (video, title) => {
    setSelectedVideo(video);
    setSelectedTitle(title);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setSelectedTitle("");
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") closeVideoModal();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Extrai o ID do YouTube de qualquer formato de URL
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return url;
  };

  return (
    <div className={classes.pageRoot}>

      {/* HERO */}
      <div className={classes.hero}>
        <div className={classes.heroBgCircle} />
        <div className={classes.heroLeft}>
          <div className={classes.heroIcon}>🎓</div>
          <div>
            <Typography className={classes.heroTitle}>Central de Ajuda</Typography>
            <Typography className={classes.heroSubtitle}>
              Tutoriais em video para aprender a usar o sistema
            </Typography>
          </div>
        </div>
        <Chip
          icon={<HelpOutlineIcon style={{ fontSize: 14 }} />}
          label={records.length + " videos"}
          className={classes.heroChip}
          size="small"
        />
      </div>

      {/* GRID */}
      <div className={classes.grid}>
        {records.length === 0 && (
          <div className={classes.emptyState}>
            <span style={{ fontSize: "3rem", opacity: 0.3 }}>🎬</span>
            <Typography style={{ fontWeight: 600, fontSize: "0.9rem" }}>
              Nenhum video disponivel
            </Typography>
          </div>
        )}
        {records.map((record, key) => {
          const videoId = extractYouTubeId(record.video);
          return (
            <div key={key} className={classes.card} onClick={() => openVideoModal(videoId, record.title)}>
              <div className={classes.thumbWrap}>
                <img
                  src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                  alt={record.title}
                  className={classes.thumb}
                  onError={(e) => { e.target.src = "https://img.youtube.com/vi/default/mqdefault.jpg"; }}
                />
                <div className={classes.playOverlay}>
                  <div className={classes.playBtn}>
                    <PlayArrowIcon style={{ color: "#0066FF", fontSize: 28 }} />
                  </div>
                </div>
              </div>
              <div className={classes.cardBody}>
                <Typography className={classes.cardTitle}>{record.title}</Typography>
                {record.description && (
                  <Typography className={classes.cardDesc}>{record.description}</Typography>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* MODAL */}
      <Modal open={Boolean(selectedVideo)} onClose={closeVideoModal} className={classes.modalOverlay}>
        <div className={classes.modalContent}>
          <div className={classes.modalHeader}>
            <Typography className={classes.modalTitle}>{selectedTitle || "Video"}</Typography>
            <IconButton size="small" className={classes.modalClose} onClick={closeVideoModal}>
              <CloseIcon style={{ fontSize: 18 }} />
            </IconButton>
          </div>
          <div className={classes.iframeWrap}>
            {selectedVideo && (
              <iframe
                className={classes.iframe}
                src={`https://www.youtube-nocookie.com/embed/${selectedVideo}?autoplay=1&rel=0&modestbranding=1`}
                title={selectedTitle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Helps;
